import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectWorkflow } from './models';
import { baseApi } from '@centerpiece/components';

export const project_workflow = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectWorkflows', 'ProjectWorkflow'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectWorkflows: builder.query<
        ProjectWorkflow[],
        { params?: object }
      >({
        query: ({ params }: { params?: object }) => ({
          url: 'project_workflow',
          method: 'GET',
          params: params,
        }),
        transformResponse: (data: ProjectWorkflow[], meta, arg) => {
          const projectContacts: Array<ProjectWorkflow> = data.map(
            (pc: ProjectWorkflow) => {
              //flatten project quote infi
              return {
                ...pc,
                // organization:
                //   pc.contact_r?.form?.organization ||
                //   pc.contact_r?.form?.custom_fields?.find(
                //     (cf: any) => cf.organization !== ''
                //   )?.organization,
                avatar: { ...pc.contact_r, relationship: pc?.id },
              };
            }
          );
          return projectContacts;
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectWorkflow' as const,
                  id,
                })),
                'ProjectWorkflows',
              ]
            : ['ProjectWorkflows'],
      }),
      getProjectWorkflow: builder.query<ProjectWorkflow, string>({
        query: id => ({
          url: `project_workflow/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectWorkflow', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectWorkflow: builder.mutation({
        query: request => ({
          url: 'project_workflow',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectWorkflows'],
      }),
      updateProjectWorkflow: builder.mutation<ProjectWorkflow, ProjectWorkflow>(
        {
          query: request => ({
            url: `project_workflow/${request.id}`,
            method: 'PUT',
            body: request,
          }),
          invalidatesTags: (result, error, arg) => [
            { type: 'ProjectWorkflow', id: arg.id },
          ],
          async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
            const patchResult = dispatch(
              project_workflow.util.updateQueryData(
                'getProjectWorkflow',
                `${id}`,
                draft => {
                  Object.assign(draft, patch);
                }
              )
            );
            try {
              await queryFulfilled;
            } catch {
              patchResult.undo();

              /**
               * Alternatively, on failure you can invalidate the corresponding cache tags
               * to trigger a re-fetch:
               * dispatch(project_workflow.util.invalidateTags(['ProjectWorkflows']))
               */
              dispatch(
                project_workflow.util.invalidateTags(['ProjectWorkflows'])
              );
            }
          },
        }
      ),
      deleteProjectWorkflow: builder.mutation<ProjectWorkflow, number>({
        query: id => ({
          url: `project_workflow/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectWorkflow' as const, arg }, 'ProjectWorkflows']
            : ['ProjectWorkflows'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectWorkflowsQuery,
  useGetProjectWorkflowQuery,
  useCreateProjectWorkflowMutation,
  useUpdateProjectWorkflowMutation,
  useDeleteProjectWorkflowMutation,
  useLazyGetProjectWorkflowsQuery,
  usePrefetch: useProjectWorkflowsPrefetch,
} = project_workflow;
const selectProjectWorkflowsRoot =
  project_workflow.endpoints.getProjectWorkflows.select({});
export const selectProjectWorkflows = createSelector(
  selectProjectWorkflowsRoot,
  project_workflow => project_workflow.data || []
);

export const subscribeProjectWorkflows =
  project_workflow.endpoints.getProjectWorkflows.useQueryState;
